import React, { useState } from "react";
import { router } from "@inertiajs/react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import OnboardingLayout from "@components/onboarding/onboarding_layout";
import ResumeUploadBox from "@components/dashboard/resume_upload_box";
import CompanyLogo from "@components/company_logo";
import { Body, SectionHeader, SectionTitle } from "@sprout/typography";
import { PrimaryButton } from "@sprout/buttons";
import { IconButton } from "@sprout/icon_button";
import CloseIcon from "@sprout/icons/close";
import { useShowFlash } from "@sprout/flash";
import useProfile from "@/queries/use_profile";
import useTranslation from "@hooks/use_translation";

import "./resume.scss";

type OnboardingJob = {
  title: string;
  companyName: string;
  logoUrl?: string;
  locations: string[];
  workType: string | null;
};

type Props = {
  job?: OnboardingJob | null;
  paths: { continue: string };
};

const WORK_TYPE_KEYS: Record<string, string> = {
  remote: "remote",
  hybrid: "hybrid",
  in_person: "inPerson",
};

const OnboardingResume = ({ job, paths }: Props) => {
  const { t } = useTranslation("onboarding", { keyPrefix: "resume" });
  const { t: tWorkType } = useTranslation("jobSearch", { keyPrefix: "workTypeOptions" });
  const { data: profile } = useProfile();
  const queryClient = useQueryClient();
  const showFlash = useShowFlash();
  const [uploadingFilename, setUploadingFilename] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const uploading = Boolean(uploadingFilename);
  const filename = profile?.resume?.filename;

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await axios.delete("/resume");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["profile"] }),
        queryClient.invalidateQueries({ queryKey: ["whoami"] }),
      ]);
    } catch {
      showFlash(t("removeError"), "error");
    } finally {
      setRemoving(false);
    }
  };

  const jobMetaLine = (applyingToJob: OnboardingJob) => {
    const workTypeKey = applyingToJob.workType ? WORK_TYPE_KEYS[applyingToJob.workType] : undefined;
    const workTypeLabel = workTypeKey ? tWorkType(workTypeKey) : null;
    return [applyingToJob.locations[0], workTypeLabel].filter(Boolean).join(" · ");
  };

  return (
    <div className="resume-onboarding">
      <div className="resume-onboarding__grid">
        <aside className="resume-onboarding__aside">
          {job && (
            <section className="resume-onboarding__job-card">
              <span className="resume-onboarding__eyebrow">{t("applyingTo")}</span>
              <div className="resume-onboarding__job-header">
                <CompanyLogo logoUrl={job.logoUrl} companyName={job.companyName} />
                <div className="resume-onboarding__job-details">
                  <SectionTitle>{job.title}</SectionTitle>
                  <Body>{job.companyName}</Body>
                  {jobMetaLine(job) && (
                    <Body metadata className="resume-onboarding__job-meta">
                      {jobMetaLine(job)}
                    </Body>
                  )}
                </div>
              </div>
            </section>
          )}

          <section className="resume-onboarding__promo-card">
            <p className="resume-onboarding__promo-title">{t("promoTitle")}</p>
            <p className="resume-onboarding__promo-body">{t("promoBody")}</p>
          </section>
        </aside>

        <div className="resume-onboarding__main">
          <section className="resume-onboarding__upload-card">
            <SectionHeader as="h1" serif large>
              {t("uploadTitle")}
            </SectionHeader>
            <Body>{t("uploadDescription")}</Body>
            {filename && !uploading && (
              <div className="resume-onboarding__uploaded-file">
                <Body>{filename}</Body>
                <IconButton
                  icon={CloseIcon}
                  size="sm"
                  label={t("removeResume")}
                  onClick={handleRemove}
                  disabled={removing}
                />
              </div>
            )}
            {(!filename || uploading) && (
              <ResumeUploadBox
                source="onboarding_resume"
                onUploadingFilenameChange={setUploadingFilename}
              />
            )}
          </section>

          <div className="resume-onboarding__actions">
            <PrimaryButton
              size="large"
              disabled={!filename || removing || uploading}
              onClick={() => router.visit(paths.continue)}
            >
              {t("continue")}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

OnboardingResume.layout = (page: React.ReactNode) => <OnboardingLayout>{page}</OnboardingLayout>;

export default OnboardingResume;
